import React, { useEffect, useState } from 'react'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Alert, Card, Segmented, Spin, Table, Tag, Tooltip as AntTooltip, Typography } from 'antd'
import type { TableColumnsType } from 'antd'
import { useAtom } from 'jotai'
import axios from 'axios'
import dayjs from 'dayjs'
import { appApiUrls } from '@shared/url-map.js'
import { TIMELINE_STATS_DAYS } from '@shared/constants.js'
import type { ProStatus, TimelineImageStats, TimelineImageStatsUser } from '@shared/common-types.js'
import { themeAtom } from '../../atoms.js'
import { getChartTheme } from '../../components/charts/chartTheme.js'

const { Text } = Typography

type StatsDays = (typeof TIMELINE_STATS_DAYS)[number]

/** 柱子从下往上的顺序：付费在最下面，最先看到 */
const PRO_STATUS_ORDER: readonly ProStatus[] = ['pro', 'free', 'unknown']

const PRO_STATUS_META: Record<ProStatus, { label: string; color: string; tag?: string }> = {
  pro: { label: '付费', color: '#2a78d6', tag: 'blue' },
  free: { label: '免费', color: '#eb6834', tag: 'orange' },
  // 未知是「没查到」，不是一类用户，用中性灰压下去
  unknown: { label: '未知', color: '#c3c2b7' },
}

type Props = {
  /** 点用户那一行，下面的列表按这个 token 搜 */
  onSelectUser: (userToken: string) => void
}

function percent(part: number, whole: number): string {
  return whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—'
}

function StatTile({ title, value, sub }: { title: string; value: React.ReactNode; sub?: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
      <div className="text-xs text-gray-500 dark:text-gray-400">{title}</div>
      <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">{value}</div>
      {sub && <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{sub}</div>}
    </div>
  )
}

function ProStatusTag({ status }: { status: ProStatus }) {
  const meta = PRO_STATUS_META[status]
  return <Tag color={meta.tag}>{meta.label}</Tag>
}

function ProNote({ stats }: { stats: TimelineImageStats }) {
  if (stats.proState.state === 'unconfigured') {
    return <Alert type="info" showIcon message="没配 POSTHOG_PERSONAL_API_KEY，付费状态都记为「未知」" />
  }
  if (stats.proState.state === 'failed') {
    return (
      <Alert type="warning" showIcon message={`PostHog 查询失败（${stats.proState.reason}），付费状态都记为「未知」`} />
    )
  }
  return null
}

function DailyChart({ stats }: { stats: TimelineImageStats }) {
  const [theme] = useAtom(themeAtom)
  const t = getChartTheme(theme)
  const rows = stats.series.map((day) => ({ ...day, label: dayjs(day.day).format('MM-DD'), ...day.byProStatus }))

  return (
    <div>
      <div className="flex flex-wrap items-center gap-4 px-1 text-xs text-gray-500 dark:text-gray-400">
        <span className="mr-auto">每日生成张数，按用户付费状态堆叠</span>
        {PRO_STATUS_ORDER.map((status) => (
          <span key={status} className="flex items-center gap-1.5">
            <span
              className="inline-block h-2.5 w-2.5 rounded-sm"
              style={{ backgroundColor: PRO_STATUS_META[status].color }}
            />
            {PRO_STATUS_META[status].label}
          </span>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: -16 }} barCategoryGap="20%">
          <CartesianGrid vertical={false} strokeDasharray="3 3" stroke={t.gridColor} />
          <XAxis
            dataKey="label"
            tick={t.axisTickProps}
            axisLine={t.axisLineProps}
            tickLine={false}
            interval="preserveStartEnd"
            minTickGap={16}
          />
          <YAxis tick={t.axisTickProps} axisLine={false} tickLine={false} allowDecimals={false} width={48} />
          <Tooltip
            cursor={{ fill: t.isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' }}
            content={({ active, payload }) => {
              const row = payload?.[0]?.payload as (typeof rows)[number] | undefined
              if (!active || !row) return null
              return (
                <div style={t.tooltipContentStyle} className="px-3 py-2 text-xs">
                  <div className="mb-1 font-semibold" style={t.tooltipLabelStyle}>
                    {row.day} · {row.total} 张 · {row.users} 人
                  </div>
                  {PRO_STATUS_ORDER.map((status) => (
                    <div key={status} className="flex items-center justify-between gap-6" style={t.tooltipItemStyle}>
                      <span className="flex items-center gap-1.5">
                        <span
                          className="inline-block h-2 w-2 rounded-sm"
                          style={{ backgroundColor: PRO_STATUS_META[status].color }}
                        />
                        {PRO_STATUS_META[status].label}
                      </span>
                      <span>{row.byProStatus[status]}</span>
                    </div>
                  ))}
                  <div className="mt-1 border-t pt-1" style={t.tooltipItemStyle}>
                    成功 {row.success} · 失败 {row.failed + row.textOnly} · 排队 {row.pending}
                  </div>
                </div>
              )
            }}
          />
          {PRO_STATUS_ORDER.map((status, i) => (
            <Bar
              key={status}
              dataKey={status}
              stackId="images"
              fill={PRO_STATUS_META[status].color}
              // 段与段之间留 2px 背景色的缝
              stroke={t.isDark ? '#1f2937' : '#fff'}
              strokeWidth={1}
              radius={i === PRO_STATUS_ORDER.length - 1 ? [4, 4, 0, 0] : 0}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

export default function OverviewCard({ onSelectUser }: Props) {
  const [days, setDays] = useState<StatsDays>(30)
  const [stats, setStats] = useState<TimelineImageStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    axios
      .get<{ code: number; data?: TimelineImageStats; msg?: string }>(appApiUrls.timelineImage.stats, {
        params: { days },
        signal: controller.signal,
      })
      .then((response) => {
        if (!response.data.data) throw new Error(response.data.msg ?? '响应数据为空')
        setStats(response.data.data)
      })
      .catch((err) => {
        if (axios.isCancel(err)) return
        console.error('获取时间线图片概览失败:', err)
        setError('获取概览失败，请稍后重试')
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [days])

  const columns: TableColumnsType<TimelineImageStatsUser> = [
    {
      title: '用户',
      dataIndex: 'userToken',
      render: (token: string) =>
        token ? (
          <AntTooltip title="在下面的列表里只看这个用户">
            <a className="font-mono text-xs" onClick={() => onSelectUser(token)}>
              {token}
            </a>
          </AntTooltip>
        ) : (
          <Text type="secondary">未留 Token</Text>
        ),
    },
    {
      title: '付费状态',
      dataIndex: 'proStatus',
      width: 96,
      filters: PRO_STATUS_ORDER.map((status) => ({ text: PRO_STATUS_META[status].label, value: status })),
      onFilter: (value, record) => record.proStatus === value,
      render: (status: ProStatus) => <ProStatusTag status={status} />,
    },
    { title: '今日', dataIndex: 'today', width: 72, align: 'right', sorter: (a, b) => a.today - b.today },
    {
      title: `近 ${stats?.days ?? days} 天`,
      dataIndex: 'total',
      width: 88,
      align: 'right',
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.total - b.total,
    },
    {
      title: '成功',
      dataIndex: 'success',
      width: 72,
      align: 'right',
      render: (success: number, record) => (
        <AntTooltip title={`成功率 ${percent(success, record.total)}`}>{success}</AntTooltip>
      ),
    },
    {
      title: '活跃天数',
      dataIndex: 'activeDays',
      width: 88,
      align: 'right',
      sorter: (a, b) => a.activeDays - b.activeDays,
    },
    {
      title: '最近生成',
      dataIndex: 'lastAt',
      width: 120,
      sorter: (a, b) => a.lastAt.localeCompare(b.lastAt),
      render: (lastAt: string) => dayjs(lastAt).format('MM-DD HH:mm'),
    },
    { title: 'App 版本', dataIndex: 'appVersion', width: 96, render: (v: string | null) => v || '--' },
  ]

  const rangePro = stats?.proBreakdown.range
  const todayPro = stats?.proBreakdown.today

  return (
    <Card
      title="分析概览"
      extra={
        <Segmented<StatsDays>
          value={days}
          options={TIMELINE_STATS_DAYS.map((value) => ({ label: `近 ${value} 天`, value }))}
          onChange={setDays}
        />
      }
    >
      {error && <Alert type="error" showIcon message={error} className="mb-4" />}
      <Spin spinning={loading && !!stats}>
        {!stats ? (
          <div className="flex h-40 items-center justify-center">{loading && <Spin />}</div>
        ) : (
          <div className="space-y-5">
            <ProNote stats={stats} />
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
              <StatTile
                title="今日生成"
                value={stats.today.total}
                sub={`昨日 ${stats.yesterday.total} · 成功 ${stats.today.success}`}
              />
              <StatTile
                title="今日生成用户"
                value={stats.today.users}
                sub={`付费 ${todayPro!.pro.users} · 免费 ${todayPro!.free.users} · 未知 ${todayPro!.unknown.users}`}
              />
              <StatTile
                title={`近 ${stats.days} 天生成`}
                value={stats.range.total}
                sub={`日均 ${(stats.range.total / stats.days).toFixed(1)} · 成功率 ${percent(stats.range.success, stats.range.total)}`}
              />
              <StatTile
                title={`近 ${stats.days} 天生成用户`}
                value={stats.range.users}
                sub={`人均 ${stats.range.users > 0 ? (stats.range.total / stats.range.users).toFixed(1) : '—'} 张`}
              />
              <StatTile
                title="付费用户占比"
                value={percent(rangePro!.pro.users, stats.range.users)}
                sub={
                  <>
                    <div>
                      付费 {rangePro!.pro.users} 人 / {rangePro!.pro.images} 张
                    </div>
                    <div>
                      免费 {rangePro!.free.users} 人 / {rangePro!.free.images} 张 · 未知 {rangePro!.unknown.users} 人 /{' '}
                      {rangePro!.unknown.images} 张
                    </div>
                  </>
                }
              />
            </div>

            <DailyChart stats={stats} />

            <div>
              <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                <Text strong>近 {stats.days} 天生成的用户</Text>
                <Text type="secondary" className="text-xs">
                  付费状态是 PostHog 上用户现在的 is_pro，不是生成那一刻的；没写过 is_pro 的老用户记为未知
                </Text>
              </div>
              <Table<TimelineImageStatsUser>
                rowKey="userToken"
                size="small"
                columns={columns}
                dataSource={stats.users}
                pagination={{ pageSize: 10, showSizeChanger: false, hideOnSinglePage: true }}
                scroll={{ x: 'max-content' }}
              />
            </div>
          </div>
        )}
      </Spin>
    </Card>
  )
}
